import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import LinearProgress from '@mui/material/LinearProgress'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import keyBy from 'lodash/keyBy'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { useGetConfiguredServiceContainersQuery } from 'api/inventory/configuredServiceContainer'
import { useAssignWorkOrderContainerMutation, useUnassignWorkOrderContainerMutation } from 'api/inventory/workOrderContainer'
import { useFetchAllMetaFiltersQuery } from 'api/meta/getMetaFilters'
import HHAccordion from 'components/common/HHAccordion'
import { getMetaFilters } from 'data/meta/metaFiltersSelectors'
import T from 'T'
import { handleError } from 'utils/error'
import { get } from 'utils/lodash'

import { isContainerDisabledForWorkOrder } from './constants'
import ContainerDetailDialog from './ContainerDetailDialog'
import ContainerListRow from './ContainerListRow'
import { countExceptionsByContainer, matchesQuery, mergeContainers } from './containerRows'
import ContainerSearchInput from './ContainerSearchInput'

import type { ExceptionShape } from '../exceptions/exception-card/types'
import type { MaterialOption, MeasureOption, OpenContainerDetails, WorkOrderContainer } from './types'

const EMPTY_CONTAINERS: WorkOrderContainer[] = []
const EMPTY_EXCEPTIONS: ExceptionShape[] = []

type WorkOrderContainersSectionProps = {
  containers?: WorkOrderContainer[]
  // Same list the Exceptions section renders; the row chip counts them per container.
  exceptions?: ExceptionShape[]
  isFetching?: boolean
  containersLoaded?: boolean
  workOrderId?: string
  configuredServiceId?: string
  // Opens the create-exception modal (owned by WODetails) pre-scoped to a container.
  onCreateException?: (containerId: string) => void
  // A failed WO-containers fetch must render as an error with a retry, never as a confident 0.
  hasContainersError?: boolean
  onRetryContainers?: () => void
}

const WorkOrderContainersSection: React.FC<WorkOrderContainersSectionProps> = ({
  containers = [],
  exceptions = EMPTY_EXCEPTIONS,
  isFetching = false,
  containersLoaded = false,
  workOrderId = '',
  configuredServiceId = '',
  onCreateException,
  hasContainersError = false,
  onRetryContainers,
}) => {
  const [searchText, setSearchText] = useState('')
  const [selectedContainer, setSelectedContainer] = useState<WorkOrderContainer | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const {
    data: serviceContainersData,
    isFetching: isServiceFetching,
    isSuccess: isServiceLoaded,
    error: serviceContainersError,
    refetch: refetchServiceContainers,
  } = useGetConfiguredServiceContainersQuery({ configuredServiceId }, { skip: !configuredServiceId })
  const [assignContainer, { isLoading: isAssigning }] = useAssignWorkOrderContainerMutation()
  const [unassignContainer, { isLoading: isUnassigning }] = useUnassignWorkOrderContainerMutation()

  const isMutating = isAssigning || isUnassigning
  const isLoading = isFetching || isServiceFetching || isMutating

  // getMetaFilters reads the fetchAllMetaFilters RTKQ cache — this subscription is what fills it
  // (deduped app-wide by RTKQ), so material/measure names resolve wherever this section renders.
  useFetchAllMetaFiltersQuery(undefined)
  const metaFilters = useSelector(getMetaFilters, shallowEqual)
  const materialKeyBy = useMemo<Record<string, MaterialOption>>(() => keyBy(get(metaFilters, 'materials', []), 'id'), [metaFilters])
  const measureKeyBy = useMemo<Record<string, MeasureOption>>(() => keyBy(get(metaFilters, 'measure', []), 'id'), [metaFilters])

  // Only treat service containers as "removed from WO" once BOTH lists have loaded; otherwise a
  // slow WO call would briefly mark assigned containers as removed.
  const serviceContainers = get(serviceContainersData, 'containers', EMPTY_CONTAINERS)
  const showRemoved = containersLoaded && isServiceLoaded
  // Softer failure than the WO list: only the "Removed from WO" enrichment is missing.
  const hasServiceContainersError = Boolean(serviceContainersError) && !serviceContainersData
  const mergedContainers = useMemo(
    () => mergeContainers(containers, showRemoved ? serviceContainers : EMPTY_CONTAINERS),
    [containers, serviceContainers, showRemoved]
  )

  const exceptionCountByContainer = useMemo(() => countExceptionsByContainer(exceptions), [exceptions])

  const rows = useMemo(
    () => mergedContainers.map((container, index) => ({ container, index, measureOption: get(measureKeyBy, container.measureId ?? '') })),
    [mergedContainers, measureKeyBy]
  )
  const filteredRows = useMemo(() => {
    const query = searchText.trim().toLowerCase()
    if (!query) return rows
    return rows.filter(({ container, index, measureOption }) => matchesQuery(container, index, query, measureOption))
  }, [rows, searchText])

  const handleSearchChange = useCallback((value: string) => setSearchText(value), [])
  const handleOpenDetails: OpenContainerDetails = useCallback(container => {
    setSelectedContainer(container)
    setIsDialogOpen(true)
  }, [])
  const handleCloseDialog = useCallback(() => setIsDialogOpen(false), [])

  const handleRowCreateException = useCallback((container: WorkOrderContainer) => onCreateException?.(container.id), [onCreateException])

  const handleToggleContainerStatus = useCallback(
    async (container: WorkOrderContainer) => {
      const disabling = !isContainerDisabledForWorkOrder(container)
      const mutate = disabling ? unassignContainer : assignContainer
      try {
        await mutate({ workOrderId, containerIds: [container.id] }).unwrap()
        toast.success(disabling ? T.CONTAINER_REMOVED_FROM_WO : T.CONTAINER_RESTORED_TO_WO)
      } catch (error) {
        handleError(error)
      }
    },
    [assignContainer, unassignContainer, workOrderId]
  )

  // No dedicated UI error surface for the service-containers fetch — keep the toast.
  useEffect(() => {
    if (serviceContainersError) handleError(serviceContainersError)
  }, [serviceContainersError])

  const handleRetryContainers = useCallback(() => onRetryContainers?.(), [onRetryContainers])

  // On a failed fetch the count is unknown — dash it (the Hero hides its chip on the same failure).
  const containerCountLabel = hasContainersError ? '—' : containers.length

  const quantityBadge = (
    <Stack alignItems="center" direction="row" gap={1}>
      <Typography color="text.secondary" variant="body2">
        {T.QUANTITY}
      </Typography>
      <Divider flexItem orientation="vertical" />
      {isFetching ? (
        <Skeleton height={24} variant="text" width={16} />
      ) : (
        <Typography fontWeight={700} variant="subtitle1">
          {containerCountLabel}
        </Typography>
      )}
    </Stack>
  )

  const retryAction = onRetryContainers ? (
    <Button color="inherit" size="small" onClick={handleRetryContainers}>
      {T.RETRY}
    </Button>
  ) : undefined

  const handleRetryServiceContainers = useCallback(() => refetchServiceContainers(), [refetchServiceContainers])

  const serviceRetryAction = (
    <Button color="inherit" size="small" onClick={handleRetryServiceContainers}>
      {T.RETRY}
    </Button>
  )

  const rowItems = filteredRows.map(({ container, index, measureOption }) => (
    <ContainerListRow
      key={container.id}
      actionPending={isMutating}
      container={container}
      exceptionCount={exceptionCountByContainer[container.id] ?? 0}
      index={index}
      measureOption={measureOption}
      onCreateException={onCreateException ? handleRowCreateException : undefined}
      onOpenDetails={handleOpenDetails}
      onToggleStatus={handleToggleContainerStatus}
    />
  ))

  const showEmpty = containersLoaded && !isLoading && filteredRows.length === 0

  return (
    <HHAccordion
      isExpandCollapseHeightSame
      isExpanded
      showCustomActions
      withNewColors
      customActionContent={quantityBadge}
      showActionIcon={false}
      summary={T.CONTAINERS}
    >
      {isLoading && <LinearProgress />}
      <Box sx={{ px: 1.5, pt: 1 }}>
        <ContainerSearchInput value={searchText} onChange={handleSearchChange} />
      </Box>

      {hasContainersError && !isFetching && (
        <Alert action={retryAction} severity="error" sx={{ mx: 1.5, my: 1 }}>
          {T.CONTAINERS_LOAD_FAILED}
        </Alert>
      )}
      {hasServiceContainersError && !isServiceFetching && (
        <Alert action={serviceRetryAction} severity="warning" sx={{ mx: 1.5, my: 1 }}>
          {T.SERVICE_CONTAINERS_LOAD_FAILED}
        </Alert>
      )}

      {rowItems}
      {showEmpty && !hasContainersError && (
        <Typography color="text.secondary" sx={{ px: 1.5, py: 2 }} variant="body2">
          {T.NO_CONTAINERS}
        </Typography>
      )}

      <ContainerDetailDialog
        container={selectedContainer}
        materialKeyBy={materialKeyBy}
        measureKeyBy={measureKeyBy}
        open={isDialogOpen}
        onClose={handleCloseDialog}
      />
    </HHAccordion>
  )
}

export default WorkOrderContainersSection
